import { isEqual } from "date-fns";
import { AlarmNotificationEndingReasonEnum } from "../../common/enums/AlarmNotificationEndingReasonEnum";
import { MemoType } from "../../common/enums/MemoType";
import { IMemoModel } from "../../common/models/IMemoModel";
import { IAlarmHistoryEventModel, IControlHistoryModel, IDataQualityEventModel, IDiagnosticEventModel, IFormEventModel, IObjectHistoryModel, IWaterLossEventModel } from "../../services/history-data-service/Models";
import { WaterLossCauseEnum } from "../../common/enums/WaterLossCauseEnum";
import i18n from "../../services/localization-service/i18n";
import { FormFeatureEnum } from "../../views/object-form-view/Models";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { IAttachment } from "../../common/models/IAttachment";

export type UseHistoryReturnType = {
    history: Array<IObjectHistoryItem>;
    totalCount: number;
    filteredCount: number;
};

export type UseHistoryOptionsType = {
    filterText: string;
    showMemos: boolean;
    showSettingsChanges: boolean;
    showAlarms: boolean;
    showDiagnosticEvents: boolean;
    showDataQualityEvents: boolean;
    showFormEvents: boolean;
    showManualEvents: boolean;
};

export type MutationContextType = {
    previousHistoryItems: IObjectHistoryModel | undefined;
};

export enum ObjectHistoryItemTypeEnum {
    Spacer = -1,
    Alarm = 0,
    Memo = 1,
    SettingChange = 2,
    DataQualityEvent = 4,
    DiagnosticEvent = 5,
    FormEvent = 6,
    WaterLossEvent = 7
}

export interface IObjectHistoryItem {
    type: ObjectHistoryItemTypeEnum;
    timestamp: Date;

    // will these ever be null? I don't see any evidence of that
    objectType?: eLynxObjectTypeEnum;
    objectID?: number;
    objectName?: string;

    // why do I have these?
    tagID?: number;
    tagName?: string;
}

export interface IObjectHistoryAlarmItem extends IObjectHistoryItem {
    discrete: boolean;
    value: number;
    listText?: string;
    units: string;
    setpointName: string;
    isAlarm: boolean;
    alarmColor: string;
    isAcknowledged: boolean;
    acknowledgedBy: string;
    acknowledgedTimestamp?: Date;
    acknowledgedColor: string;
    alarmEndingReason?: AlarmNotificationEndingReasonEnum;
}

export interface IObjectHistorySettingChangeItem extends IObjectHistoryItem {
    newValue: string;
    newValueNumber: number;
    oldValue: string;
    oldValueNumber: number;
    units?: string;
    userName?: string;
}

export interface IObjectHistoryMemoItem extends IObjectHistoryItem {
    memoID: number;
    text: string;
    userName: string;
    userID: number;
    memoType: MemoType;

    attachments: IAttachment[]
}

export interface IObjectHistoryDataQualityEventItem extends IObjectHistoryItem {
    createdBy: string;
    ruleName?: string;
    isStart: boolean;
    startTime: Date;
    endTime?: Date;
    manual: boolean;
}

export interface IObjectHistoryDiagnosticEventItem extends IObjectHistoryItem {
    eventID?: string;
    createdBy: string;
    ruleName?: string;
    ruleID?: number;
    isStart: boolean;
    startTime: Date;
    endTime?: Date;
    note: string;
    manual: boolean;
}

export interface IObjectHistoryFormEventItem extends IObjectHistoryItem {
    createdBy: string;
    isStart: boolean;
    startTime: Date;
    endTime?: Date;
    formName: string;
    formFeatures: FormFeatureEnum;
    manual: boolean;
}

export interface IObjectHistoryWaterLossEventItem extends IObjectHistoryItem {
    id?: string;
    createdBy: string;
    isStart: boolean;
    startTime: Date;
    endTime?: Date;
    estimatedLoss?: number;
    estimatedLossUOM?: number;
    cause?: WaterLossCauseEnum;
    notes?: string;
    manual: boolean;
}

export function selectHistory(data: IObjectHistoryModel, options?: UseHistoryOptionsType): UseHistoryReturnType {

    const shapedData = shapeData(data);
    const filteredData = options
        ? filterData(shapedData, options)
        : shapedData;

    return {
        history: filteredData,
        totalCount: shapedData?.length ?? 0,
        filteredCount: filteredData.length ?? 0
    };
}

function shapeData(data: IObjectHistoryModel): Array<IObjectHistoryItem> {

    const alarmItems: Array<IObjectHistoryAlarmItem> = data.alarmHistory.map(model => {
        return createObjectHistoryAlarmItem(model);
    });

    const settingChangeItems: Array<IObjectHistorySettingChangeItem> = data.controlHistory.map(model => {
        return createObjectHistorySettingChangeItem(model);
    });

    const memoItems: Array<IObjectHistoryMemoItem> = data.memos.map(model => {
        return createObjectHistoryMemoItem(model);
    });

    const dataQualityEventItems = data.dataQualityEvents
        ? data.dataQualityEvents
            .map(o =>
                createDataQualityEventItems(o))
            .reduce(function (a, b) {
                return a.concat(b);
            }, [])
        : new Array<IObjectHistoryDataQualityEventItem>();

    const diagnosticEventItems = data.diagnosticEvents
        ? data.diagnosticEvents
            .map(o =>
                createDiagnosticEventItems(o))
            .reduce(function (a, b) {
                return a.concat(b);
            }, [])
        : new Array<IObjectHistoryDiagnosticEventItem>();

    const formEventItems = data.formEvents
        ? data.formEvents
            .map(o =>
                createFormEventItems(o))
            .reduce(function (a, b) {
                return a.concat(b);
            }, [])
        : new Array<IObjectHistoryFormEventItem>();

    const waterLossEventItems = data.waterLossEvents
        ? data.waterLossEvents
            .map(o =>
                createWaterLossEventItems(o))
            .reduce(function (a, b) {
                return a.concat(b);
            }, [])
        : new Array<IObjectHistoryWaterLossEventItem>();

    // return [...[
    //     ...alarmItems,
    //     ...settingChangeItems,
    //     ...memoItems,
    //     ...dataQualityEventItems,
    //     ...diagnosticEventItems,
    // ]
    //     .sort((a, b) => (b.timestamp.valueOf() ?? 0) - (a.timestamp.valueOf() ?? 0)),
    // {
    //     type: ObjectHistoryItemTypeEnum.Spacer,
    //     timestamp: new Date()
    // }];

    return [
        ...alarmItems,
        ...settingChangeItems,
        ...memoItems,
        ...dataQualityEventItems,
        ...diagnosticEventItems,
        ...formEventItems,
        ...waterLossEventItems
    ].sort((a, b) => (b.timestamp.valueOf() ?? 0) - (a.timestamp.valueOf() ?? 0));
}

function filterData(history: Array<IObjectHistoryItem>, options: UseHistoryOptionsType): Array<IObjectHistoryItem> {
    //User Story 53044: Research - 2025 Q2 - Review GAS report
    // let filtered: Array<IObjectHistoryItem> = new Array<IObjectHistoryItem>();
    const filterText = options.filterText.toLowerCase();

    const filtered = history.filter(item => {

        switch (item.type) {
            case ObjectHistoryItemTypeEnum.Alarm:

                const alarmItem = item as IObjectHistoryAlarmItem;
                // well and tank battery version
                // doesn't look for object name for other types
                return options.showAlarms &&
                    ((alarmItem.tagName?.toLowerCase().indexOf(filterText) ?? -1) !== -1 ||
                        (alarmItem.objectName?.toLowerCase().indexOf(filterText) ?? -1) !== -1 ||
                        alarmItem.setpointName.toLowerCase().indexOf(filterText) !== -1 ||
                        filterText === "");

            case ObjectHistoryItemTypeEnum.Memo:

                const memoItem = item as IObjectHistoryMemoItem;
                // well and tank battery version
                // doesn't look for tag name for other types
                return options.showMemos &&
                    (memoItem.text.toLowerCase().indexOf(filterText) !== -1 ||
                        (memoItem.tagName?.toLowerCase().indexOf(filterText) ?? -1) !== -1 ||
                        memoItem.userName.toLowerCase().indexOf(filterText) !== -1 ||
                        filterText === "");

            case ObjectHistoryItemTypeEnum.SettingChange:
                // well and tank battery version
                // doesn't look for object name for other types
                const settingsItem = item as IObjectHistorySettingChangeItem;
                return options.showSettingsChanges &&
                    ((settingsItem.objectName?.toLowerCase().indexOf(filterText) ?? -1) !== -1 ||
                        (settingsItem.tagName?.toLowerCase().indexOf(filterText) ?? -1) !== -1 ||
                        (settingsItem.userName?.toLowerCase().indexOf(filterText) ?? -1) !== -1 ||
                        filterText === "");

            case ObjectHistoryItemTypeEnum.DataQualityEvent:

                const dataQualityItem = item as IObjectHistoryDataQualityEventItem;
                return ((options.showDataQualityEvents && !dataQualityItem.manual) ||
                        (options.showManualEvents && dataQualityItem.manual)) &&
                    (dataQualityItem.createdBy.toLowerCase().indexOf(filterText) !== -1 ||
                        (dataQualityItem.objectName?.toLowerCase().indexOf(filterText) ?? -1) !== -1 ||
                        (dataQualityItem.ruleName?.toLowerCase().indexOf(filterText) ?? -1) !== -1 ||
                        filterText === "");

            case ObjectHistoryItemTypeEnum.DiagnosticEvent:
                const diagnosticItem = item as IObjectHistoryDiagnosticEventItem;
                return ((options.showDiagnosticEvents && !diagnosticItem.manual) ||
                        (options.showManualEvents && diagnosticItem.manual)) &&
                    (diagnosticItem.createdBy.toLowerCase().indexOf(filterText) !== -1 ||
                        (diagnosticItem.objectName?.toLowerCase().indexOf(filterText) ?? -1) !== -1 ||
                        (diagnosticItem.ruleName?.toLowerCase().indexOf(filterText) ?? -1) !== -1 ||
                        filterText === "");

            case ObjectHistoryItemTypeEnum.FormEvent:
                const formItem = item as IObjectHistoryFormEventItem;
                return ((options.showFormEvents && !formItem.manual) ||
                        (options.showManualEvents && formItem.manual)) &&
                    (formItem.createdBy.toLowerCase().indexOf(filterText) !== -1 ||
                        (formItem.objectName?.toLowerCase().indexOf(filterText) ?? -1) !== -1 ||
                        (formItem.formName?.toLowerCase().indexOf(filterText) ?? -1) !== -1 ||
                        filterText === "");

            case ObjectHistoryItemTypeEnum.WaterLossEvent:
                const waterLossItem = item as IObjectHistoryWaterLossEventItem;
                return options.showManualEvents &&
                    (waterLossItem.createdBy.toLowerCase().indexOf(filterText) !== -1 ||
                        (waterLossItem.objectName?.toLowerCase().indexOf(filterText) ?? -1) !== -1 ||
                        filterText === "");

            // case ObjectHistoryItemTypeEnum.Spacer:
            //     return true;
            default:
                return false;

        }
    });

    return filtered;
}

function createObjectHistoryMemoItem(model: IMemoModel): IObjectHistoryMemoItem {
    return {
        type: ObjectHistoryItemTypeEnum.Memo,
        timestamp: new Date(model.timestamp),

        objectType: model.object?.objectType,
        objectID: model.object?.id,
        objectName: model.object?.objectName,

        tagID: model.object?.objectType === eLynxObjectTypeEnum.Tag
            ? model.object?.id
            : undefined,
        tagName: model.object?.objectType === eLynxObjectTypeEnum.Tag
            ? model.object?.objectName
            : undefined,

        memoID: model.memoID,
        text: model.text,
        userName: model.userName ?? "",
        userID: model.userID ?? 0,
        memoType: model.memoType,
        attachments: model.attachments ?? []
    };
}

function createObjectHistorySettingChangeItem(model: IControlHistoryModel): IObjectHistorySettingChangeItem {
    return {
        type: ObjectHistoryItemTypeEnum.SettingChange,
        timestamp: new Date(model.timestamp),

        objectType: model.object?.objectType,
        objectID: model.object?.id,
        objectName: model.object?.objectName,

        tagID: model.objectID,
        tagName: model.objectName,

        newValue: model.newValue,
        newValueNumber: model.newValueNumber,
        oldValue: model.oldValue,
        oldValueNumber: model.oldValueNumber,
        units: model.uom,
        userName: model.userName
    };
}

function createObjectHistoryAlarmItem(model: IAlarmHistoryEventModel): IObjectHistoryAlarmItem {
    return {
        type: ObjectHistoryItemTypeEnum.Alarm,
        timestamp: new Date(model.timestamp),

        objectType: model.object?.objectType,
        objectID: model.object?.id,
        objectName: model.object?.objectName,

        tagID: model.tagID,
        tagName: model.tagName,

        discrete: model.discrete,
        value: model.value,
        listText: model.listText,
        units: model.uom,

        setpointName: model.setPointName,

        isAlarm: model.inAlarm,
        alarmColor: model.alarmColor,

        isAcknowledged: model.acknowledged,
        acknowledgedBy: model.acknowledgedBy,
        acknowledgedTimestamp: model.acknowledgedTimestamp
            ? new Date(model.acknowledgedTimestamp)
            : undefined,
        acknowledgedColor: model.ackColor,
        alarmEndingReason: model.alarmEndingReason
    };
}

function createDiagnosticEventItems(model: IDiagnosticEventModel): Array<IObjectHistoryDiagnosticEventItem> {
    const startTime = new Date(model.startTime);
    const endTime = model.endTime ? new Date(model.endTime) : undefined;

    let diagnostic = new Array<IObjectHistoryDiagnosticEventItem>(
        // Diagnostic Start
        {
            type: ObjectHistoryItemTypeEnum.DiagnosticEvent,
            timestamp: new Date(model.startTime),

            objectType: model.object?.objectType,
            objectID: model.object?.id,
            objectName: model.object?.objectName,

            createdBy: model.createdBy?.length > 0 ? model.createdBy.replace("eLynx Rules Engine", "eLynx") : model.createdBy,
            ruleName: model.ruleName,
            isStart: true,
            startTime: startTime,
            endTime: endTime,
            note: model.note,
            manual: model.manual
        });

    if (endTime) {
        diagnostic.push(
            // Diagnostic End
            {
                type: ObjectHistoryItemTypeEnum.DiagnosticEvent,
                timestamp: new Date(model.endTime),

                objectType: model.object?.objectType,
                objectID: model.object?.id,
                objectName: model.object?.objectName,

                createdBy: model.createdBy?.length > 0 ? model.createdBy.replace("eLynx Rules Engine", "eLynx") : model.createdBy,
                ruleName: model.ruleName,
                isStart: false,
                startTime: startTime,
                endTime: endTime,
                note: model.note,
                manual: model.manual
            }
        );
    }

    return diagnostic;
}

function createDataQualityEventItems(model: IDataQualityEventModel): Array<IObjectHistoryDataQualityEventItem> {
    const startTime = new Date(model.startTime);
    const endTime = model.endTime ? new Date(model.endTime) : undefined;

    let dataQuality = new Array<IObjectHistoryDataQualityEventItem>(
        // Data Quality Start
        {
            type: ObjectHistoryItemTypeEnum.DataQualityEvent,
            timestamp: new Date(model.startTime),

            objectType: model.object?.objectType,
            objectID: model.object?.id,
            objectName: model.object?.objectName,

            tagName: model.tagName,

            createdBy: model.createdBy?.length > 0 ? model.createdBy.replace("eLynx Rules Engine", "eLynx") : model.createdBy,
            ruleName: model.ruleName,
            isStart: true,
            startTime: startTime,
            endTime: endTime,
            manual: model.manual
        }
    );

    if (model.endTime) {
        dataQuality.push(
            // Data Quality End
            {
                type: ObjectHistoryItemTypeEnum.DataQualityEvent,
                timestamp: new Date(model.endTime),

                objectType: model.object?.objectType,
                objectID: model.object?.id,
                objectName: model.object?.objectName,

                tagName: model.tagName,

                createdBy: model.createdBy?.length > 0 ? model.createdBy.replace("eLynx Rules Engine", "eLynx") : model.createdBy,
                ruleName: model.ruleName,
                isStart: false,
                startTime: startTime,
                endTime: endTime,
                manual: model.manual
            } as IObjectHistoryDataQualityEventItem
        );
    }

    return dataQuality;
}

function createFormEventItems(model: IFormEventModel): Array<IObjectHistoryFormEventItem> {
    const startTime = new Date(model.startTime);
    const endTime = model.endTime ? new Date(model.endTime) : undefined;

    // Form Start
    let Form = new Array<IObjectHistoryFormEventItem>({
        type: ObjectHistoryItemTypeEnum.FormEvent,
        timestamp: new Date(model.startTime),

        objectType: model.object?.objectType,
        objectID: model.object?.id,
        objectName: model.object?.objectName,

        formName: model.formName,
        formFeatures: model.formFeatures,

        createdBy: model.createdBy,
        isStart: true,
        startTime: startTime,
        endTime: endTime,
        manual: model.manual
    });

    if (((model.formFeatures & FormFeatureEnum.allowClose) === FormFeatureEnum.allowClose) &&
        endTime &&
        !isEqual(endTime, startTime)) {

        // Form End
        Form.push({
            type: ObjectHistoryItemTypeEnum.FormEvent,
            timestamp: new Date(model.endTime),

            objectType: model.object?.objectType,
            objectID: model.object?.id,
            objectName: model.object?.objectName,

            formName: model.formName,
            formFeatures: model.formFeatures,

            createdBy: model.createdBy,
            isStart: false,
            startTime: startTime,
            endTime: endTime,
            manual: model.manual
        });
    }

    return Form;
}

function createWaterLossEventItems(model: IWaterLossEventModel): Array<IObjectHistoryWaterLossEventItem> {
    const startTime = new Date(model.startTime);
    const endTime = model.endTime ? new Date(model.endTime) : undefined;

    let waterLoss = new Array<IObjectHistoryWaterLossEventItem>(
        // Data Quality Start
        {
            type: ObjectHistoryItemTypeEnum.WaterLossEvent,
            timestamp: new Date(model.startTime),
            objectType: model.object?.objectType,
            objectID: model.object?.id,
            objectName: model.object?.objectName,

            id: model.id,
            createdBy: model.createdBy?.length > 0 ? model.createdBy.replace("eLynx Rules Engine", "eLynx") : model.createdBy,
            isStart: true,
            startTime: startTime,
            endTime: endTime,

            estimatedLoss: model.estimatedLoss,
            estimatedLossUOM: model.estimatedLossUOM,
            cause: model.cause,
            notes: model.notes,
            manual: model.manual
        }
    );

    if (model.endTime) {
        waterLoss.push(
            // Data Quality End
            {
                type: ObjectHistoryItemTypeEnum.WaterLossEvent,
                timestamp: new Date(model.endTime),
                objectType: model.object?.objectType,
                objectID: model.object?.id,
                objectName: model.object?.objectName,

                id: model.id,
                createdBy: model.createdBy?.length > 0 ? model.createdBy.replace("eLynx Rules Engine", "eLynx") : model.createdBy,
                isStart: false,
                startTime: startTime,
                endTime: endTime,

                estimatedLoss: model.estimatedLoss,
                estimatedLossUOM: model.estimatedLossUOM,
                cause: model.cause,
                notes: model.notes,
                manual: model.manual
            } as IObjectHistoryWaterLossEventItem
        );
    }

    return waterLoss;
}

export function toMemoModel(memo: IObjectHistoryMemoItem): IMemoModel {
    return {
        memoID: memo.memoID,
        timestamp: memo.timestamp,
        text: memo.text,
        memoType: memo.memoType,
        object: {
            id: memo.objectID as number,
            objectType: memo.objectType as eLynxObjectTypeEnum
        },
        userName: memo.userName,
        userID: memo.userID,
        attachments: memo.attachments
    };
}

export function toDiagnosticEventModel(item: IObjectHistoryDiagnosticEventItem): IDiagnosticEventModel {
    return {
        id: item.eventID ?? "",
        ruleName: item.ruleName ?? "",
        ruleID: item.ruleID,
        note: item.note,
        object: {
            id: item.objectID as number,
            objectType: item.objectType as eLynxObjectTypeEnum
        },
        startTime: item.startTime,
        endTime: item.endTime ?? "",
        createdBy: item.createdBy,
        createdTime: item.timestamp,
        manual: item.manual
    };
}

export function toWaterLossEventModel(item: IObjectHistoryWaterLossEventItem): IWaterLossEventModel {
    return {
        id: item.id ?? "",
        estimatedLoss: item.estimatedLoss,
        estimatedLossUOM: item.estimatedLossUOM,
        cause: item.cause,
        notes: item.notes,
        object: {
            id: item.objectID as number,
            objectType: item.objectType as eLynxObjectTypeEnum
        },
        startTime: item.startTime,
        endTime: item.endTime ?? "",
        createdBy: item.createdBy,
        createdTime: item.timestamp,
        manual: item.manual
    };
}

export function getObjectHistoryItemTypeName(item: IObjectHistoryItem) {
    return item.type === ObjectHistoryItemTypeEnum.Memo
        ? i18n.t("dataTypeNames.memo")
        : item.type === ObjectHistoryItemTypeEnum.DiagnosticEvent ||
          item.type === ObjectHistoryItemTypeEnum.WaterLossEvent
            ? i18n.t("dataTypeNames.event")
            : "Unknown";
}